import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { Appearance, Me, Theme } from "../api/types.gen.ts";
import { endonym, LANGUAGES, t } from "../i18n/index.ts";
import { barLabels, setBarLabels } from "../lib/barLabels.ts";
import { bump, useAction } from "../lib/resource.ts";
import { applyPrefs, me, mySpaces } from "../lib/state.ts";
import { applyTheme, type ThemeChoice } from "../lib/theme.ts";
import { toast } from "../lib/toast.ts";
import { useId } from "../lib/uid.ts";
import { browserZone, zoneOptions } from "../lib/zone.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { Button } from "../ui/Button.tsx";
import { Field, Form, Input, Select, Switch } from "../ui/form.tsx";
import { Section } from "../ui/misc.tsx";
import { PictureField } from "../ui/PictureEditor.tsx";
import { RowList } from "../ui/RowList.tsx";
import { Segmented } from "../ui/Segmented.tsx";
import { MentionsEmailField } from "./account/mentionsEmail.tsx";
import { AccountTabs } from "./account/tabs.tsx";
import { Address, TriageEmailAddressActions } from "./account/triageEmailAddressActions.tsx";
import { PersonalAgents } from "./settings/personalAgent.tsx";

/** Language names in their own language ("Português (Brasil)"). */

/**
 * The theme, and whether dark is true black: each applied as it's picked (no Save), then kept in your profile; back
 * to the one before if that fails.
 */
function ThemeField() {
  const u = me.value as Me;
  const chosen: ThemeChoice = { theme: u.theme, trueBlack: u.true_black };
  const [pick] = useAction(async (change: { theme: Theme } | { true_black: boolean }) => {
    applyTheme("theme" in change ? { ...chosen, theme: change.theme } : { ...chosen, trueBlack: change.true_black });
    try {
      const saved = await api.patch<Me>("/me", change);
      me.value = { ...(me.value as Me), theme: saved.theme, true_black: saved.true_black };
    } catch (e) {
      applyTheme(chosen);
      throw e;
    }
  });
  const blackHint = useId();
  return (
    <>
      <Field label={t("profile.theme")} hint={t("profile.theme.hint")} group={true} anchor="theme">
        <Segmented
          label={t("profile.theme")}
          value={chosen.theme}
          onValue={(v) => v !== chosen.theme && pick({ theme: v })}
          options={(["system", "light", "dark"] as const).map((v) => ({ value: v, label: t(`profile.theme.${v}`) }))}
          class="self-start"
        />
      </Field>
      <div class="flex flex-col gap-1.5">
        <Switch
          label={t("profile.theme.trueBlack")}
          checked={chosen.trueBlack}
          onCheck={(on) => pick({ true_black: on })}
          aria-describedby={blackHint}
          class="self-start"
        />
        <p id={blackHint} class="text-xs text-zinc-500">
          {t("profile.theme.trueBlack.hint")}
        </p>
      </div>
    </>
  );
}

/** How entities are drawn: applied as it's picked (no Save), then kept in your profile; back to before if that fails. */
function AppearanceField() {
  const u = me.value as Me;
  const [pick] = useAction(async (appearance: Appearance) => {
    me.value = { ...u, appearance };
    try {
      const saved = await api.patch<Me>("/me", { appearance });
      me.value = { ...(me.value as Me), appearance: saved.appearance };
    } catch (e) {
      me.value = { ...(me.value as Me), appearance: u.appearance };
      throw e;
    }
  });
  return (
    <Field
      label={t("profile.appearance")}
      hint={t(`profile.appearance.${u.appearance}.hint`)}
      group={true}
      anchor="appearance"
    >
      <Segmented
        label={t("profile.appearance")}
        value={u.appearance}
        onValue={(v) => v !== u.appearance && pick(v)}
        options={(["icons", "pills"] as const).map((v) => ({ value: v, label: t(`profile.appearance.${v}`) }))}
        class="self-start"
      />
    </Field>
  );
}

/** How a page's actions show on a narrow screen, on this device (lib/barLabels.ts): applied as it's picked. */
function BarLabelsField() {
  const [labels, setLabels] = useState(barLabels);
  return (
    <Field label={t("profile.barLabels")} hint={t("profile.barLabels.hint")} group={true} anchor="bar-labels">
      <Segmented
        label={t("profile.barLabels")}
        value={labels ? "both" : "icons"}
        onValue={(v) => {
          setBarLabels(v === "both");
          setLabels(v === "both");
        }}
        options={(["icons", "both"] as const).map((v) => ({ value: v, label: t(`profile.barLabels.${v}`) }))}
        class="self-start"
      />
    </Field>
  );
}

/** Your picture: uploaded (cropped first) and removed on their own, not with Save. */
function PictureSetting() {
  const u = me.value as Me;
  const saved = (next: Me, message: string) => {
    me.value = next;
    bump(); // the members lists, where others' pictures come from
    toast(message);
  };
  return (
    <PictureField
      label={t("profile.picture")}
      hint={t("profile.picture.hint")}
      round={true}
      has={Boolean(u.avatar)}
      preview={<Avatar id={u.id} name={u.name} kind={u.kind} picture={u.avatar ?? null} size={64} />}
      onUpload={async (picture) => saved(await api.putFile<Me>("/me/avatar", picture), t("profile.picture.saved"))}
      onRemove={async () => saved(await api.del<Me>("/me/avatar"), t("profile.picture.removed"))}
    />
  );
}

export function Profile() {
  const u = me.value as Me;
  const [name, setName] = useState(u.name);
  const [lang, setLang] = useState(u.locale ?? "");
  const [zone, setZone] = useState(u.time_zone ?? "");
  const [save, saving] = useAction(async () => {
    const next = await api.patch<Me>("/me", { name, locale: lang || null, time_zone: zone || null });
    me.value = next;
    applyPrefs(next);
    bump(); // refetch server-localized content in the new language
    toast(t("profile.saved"));
  });
  return (
    <>
      <AccountTabs current="/profile" />
      <div class="mb-4">
        <PictureSetting />
      </div>
      <Form class="flex flex-col gap-4" onSubmit={save}>
        <Field label={t("profile.name")}>
          <Input value={name} onValue={setName} required={true} />
        </Field>
        <Field label={t("profile.email")}>
          <Input value={u.email ?? ""} disabled={true} />
        </Field>
        <Field label={t("profile.language")} anchor="language">
          <Select
            value={lang}
            onValue={setLang}
            options={[
              { value: "", label: t("profile.browserDefault") },
              ...LANGUAGES.map((l) => ({ value: l, label: endonym(l) })),
            ]}
          />
        </Field>
        <Field label={t("profile.timeZone")} hint={t("profile.timeZoneHint")} anchor="time-zone">
          <Select
            value={zone}
            onValue={setZone}
            options={[{ value: "", label: t("profile.browserZone", { zone: browserZone() }) }, ...zoneOptions()]}
          />
        </Field>
        <AppearanceField />
        <ThemeField />
        <BarLabelsField />
        <MentionsEmailField />
        <div class="flex justify-end">
          <Button variant="primary" submit={true} busy={saving} answer="confirm">
            {t("common.save")}
          </Button>
        </div>
      </Form>
      {mySpaces().some((s) => s.triage_email_address) ? (
        <Section title={t("profile.triageEmailAddresses")} anchor="triage-email-addresses">
          <p class="mb-3 text-sm text-zinc-600">{t("profile.triageEmailAddressesHint")}</p>
          <RowList
            rows={mySpaces().flatMap((s) =>
              s.triage_email_address
                ? [
                    {
                      key: s.space,
                      title: u.orgs.length > 1 || s.org.spaces.length > 1 ? `${s.org.name} · ${s.name}` : s.name,
                      detail: <Address address={s.triage_email_address} />,
                      aside: <TriageEmailAddressActions space={s.space} user={u.id} address={s.triage_email_address} />,
                    },
                  ]
                : [],
            )}
          />
        </Section>
      ) : null}
      <PersonalAgents />
    </>
  );
}
